import React, { useState, useEffect } from "react";
import { Table, Input, Button, Space, Popconfirm, Tag, message, Card, Typography, Select, Switch, Col, Row, Modal, Divider } from "antd";
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, BellOutlined, CheckCircleOutlined, CloseCircleOutlined, TagOutlined, ShareAltOutlined, InfoCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import OfferServ from "../../service/OfferServ"; import UserServ from "../../service/UserServ"; import AddOffer from "../../component/AddOffer";
import Breadcrumbs from "../../component/Breadcrumbs";
import { formatDate } from "../../util/dateUtils";
import EmptyState from "../../util/EmptyState";
import GlobalTooltip from "../../component/GlobalTooltip";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import { getImageUrl } from "../../component/SafeImage";

const { Text } = Typography;
const { Option } = Select;

const Offers = () => {
    const [searchText, setSearchText] = useState("");
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addOfferModal, setAddOfferModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [offerDetails, setOfferDetails] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalOffers, setTotalOffers] = useState(0);
    const [users, setUsers] = useState([]);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [viewModalOffer, setViewModalOffer] = useState(null);

    const handleViewDetails = (record) => {
        setViewModalOffer(record);
        setViewModalVisible(true);
    };

    const fetchOffers = async (p = page) => {
        try {
            setLoading(true);
            const params = {
                limit: pageSize,
                page: p,
                search: searchText
            };
            const response = await OfferServ.getAll(params);
            setOffers(Array.isArray(response?.data) ? response?.data : []);
            setTotalOffers(response?.pagination?.total || 0);
        } catch (error) {
            console.error("Error fetching offers:", error);
            message.error("Failed to fetch offers");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await UserServ.index({ page: 1, limit: 15, sort: JSON.stringify({ createdAt: -1 }) });
            setUsers(Array.isArray(response?.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    useEffect(() => {
        fetchOffers();
        fetchUsers();
    }, [page, pageSize, searchText]);

    const handleAddOffer = () => {
        setOfferDetails(null);
        setIsEditing(false);
        setAddOfferModal(true);
    };

    const handleEdit = (record) => {
        setOfferDetails(record);
        setIsEditing(true);
        setAddOfferModal(true);
    };

    const handleDelete = async (record) => {
        try {
            await OfferServ.remove(record._id);
            message.success("Offer removed successfully");
            fetchOffers();
        } catch (error) {
            message.error("Failed to remove offer");
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            await OfferServ.update(record._id, { isActive: !record.isActive });
            message.success(`Offer ${!record.isActive ? 'activated' : 'deactivated'} successfully`);
            fetchOffers();
        } catch (error) {
            message.error("Failed to update status");
        }
    };

    const isOfferLive = (offer) => {
        if (!offer.isLive) return false;
        if (!offer.expiryDate) return true;
        return new Date(offer.expiryDate) > new Date();
    };

    const columns = [
        {
            title: "Offer Image",
            dataIndex: "imageUrl",
            key: "imageUrl",
            width: 100,
            render: (imageUrl) => (
                <div style={{ padding: '8px 0' }}>
                    {imageUrl ? (
                        <div style={{
                            width: 80,
                            height: 60,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}>
                            <img
                                src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'https://api.nearzon.com'}${imageUrl}`}
                                alt="Offer"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>
                    ) : (
                        <div style={{
                            width: 80,
                            height: 60,
                            borderRadius: '12px',
                            background: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px border-dashed #cbd5e1'
                        }}>
                            <TagOutlined style={{ color: '#94a3b8', fontSize: '20px' }} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Offer Details",
            key: "details",
            render: (_, record) => (
                <div style={{ maxWidth: '300px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>{record.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {record.description}
                    </div>
                </div>
            ),
        },
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            render: (code) => (
                <div style={{
                    background: '#f0f7ff',
                    padding: '4px 12px',
                    borderRadius: 8,
                    border: '1.5px dashed #4776E6',
                    display: 'inline-block'
                }}>
                    <Text strong style={{ color: '#4776E6', letterSpacing: 0.5, fontSize: '12px' }}>{code || "NO CODE"}</Text>
                </div>
            ),
        },
        {
            title: "Discount",
            dataIndex: "discount",
            key: "discount",
            render: (discount) => (
                <span style={{
                    color: '#722ed1',
                    background: '#f9f0ff',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontWeight: 800,
                    fontSize: '12px',
                    border: '1px solid #efdbff'
                }}>
                    {discount || "0"}
                </span>
            ),
        },
        {
            title: "Live",
            dataIndex: "isLive",
            key: "isLive",
            width: 140,
            render: (isLive, record) => (
                <Switch
                    className={isLive ? "pcu-switch-live" : "pcu-switch-offline"}
                    checkedChildren="Online"
                    unCheckedChildren="Offline"
                    checked={isLive}
                    onChange={(checked) => handleToggleStatus(record, checked)}
                    onClick={(checked, e) => e.stopPropagation()}
                    style={{ backgroundColor: isLive ? '#22c55e' : '#ef4444' }}
                />
            ),
        },
        {
            title: "Expiry",
            dataIndex: "expiryDate",
            key: "expiryDate",
            render: (date) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CalendarOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                        {date ? formatDate(date) : "Forever"}
                    </span>
                </div>
            ),
        },
        {
            title: "Performance",
            dataIndex: "usedCount",
            key: "usedCount",
            render: (count) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#1e293b',
                        border: '1px solid #f1f5f9'
                    }}>
                        {count || 0}
                    </div>
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>Uses</span>
                </div>
            ),
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        }
    ];

    const topRef = React.useRef(null);

    return (
        <div className="sd-container" style={{ padding: '24px' }} ref={topRef}>
            <div className="pcu-margin-bottom-20">
                <Breadcrumbs />
            </div>
            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <TagOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Marketing Offers</h1>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{totalOffers} offers configured</p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ResetButton
                        icon={<ReloadOutlined />}
                        onClick={fetchOffers}
                    />
                    <PrimaryButton
                        icon={<PlusOutlined />}
                        onClick={handleAddOffer}
                    />
                </div>
            </div>

            <div className="pcu-padding-0">
                {/* Summary Stats Cards */}
                <Row gutter={[16, 16]} className="pcu-margin-bottom-24">
                    <Col xs={24} sm={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)',
                            padding: '20px',
                            borderRadius: '16px',
                            border: '1px solid #d1e9ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(24, 144, 255, 0.04)'
                        }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <TagOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#0050b3', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        Total Offers
                                    </span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#1890ff' }}>{totalOffers}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f6ffed 0%, #f0fdf4 100%)',
                            padding: '20px',
                            borderRadius: '16px',
                            border: '1px solid #dcfce7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.04)'
                        }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#237804', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        Live Promotions
                                    </span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#52c41a' }}>
                                    {offers.filter(o => isOfferLive(o)).length}
                                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#94a3b8', marginLeft: '6px' }}>on page</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #fff7e6 0%, #fffbeb 100%)',
                            padding: '20px',
                            borderRadius: '16px',
                            border: '1px solid #fef3c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(250, 140, 22, 0.04)'
                        }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <ShareAltOutlined style={{ color: '#fa8c16', fontSize: '18px' }} />
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                        Total Uses
                                    </span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, color: '#fa8c16' }}>
                                    {offers.reduce((sum, o) => sum + (o.usedCount || 0), 0)}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <FilterBar>
                    <Col xs={24} md={10}>
                        <SearchInput
                            placeholder="Search by title or code..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>
                </FilterBar>

                <div className="pcu-border-top pcu-margin-top-16">
                    <div className="pcu-padding-y-16 pcu-font-16 pcu-font-800 pcu-color-title" style={{ letterSpacing: '-0.3px' }}>Offer Catalog</div>
                    <DataTable
                        columns={columns}
                        data={offers}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: totalOffers,
                            onChange: (p, ps) => {
                                setPage(p);
                                setPageSize(ps);
                                topRef.current?.scrollIntoView({ behavior: 'smooth' });
                            },
                            showSizeChanger: true,
                            showTotal: (total) => <span style={{ fontWeight: 700, color: '#ff7a00', fontSize: '12px' }}>Total {total} items</span>
                        }}
                    />
                </div>
            </div>

            {/* Offer Details Modal */}
            <Modal
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={null}
                width={500}
                centered
                closable={true}
                bodyStyle={{ padding: '0' }}
            >
                {viewModalOffer && (
                    <div style={{ overflow: 'hidden', borderRadius: '12px' }}>
                        {/* Modal Header */}
                        <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ width: '60px', height: '60px', background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                {viewModalOffer.imageUrl ? (
                                    <img src={getImageUrl(viewModalOffer.imageUrl)} alt="Offer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <TagOutlined style={{ fontSize: '24px', color: '#4776E6' }} />
                                    </div>
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#1e293b' }}>{viewModalOffer.title}</h2>
                                    {isOfferLive(viewModalOffer) ? (
                                        <Tag color="success" style={{ borderRadius: '8px', padding: '0 8px', fontWeight: 700, fontSize: '9px' }}>LIVE</Tag>
                                    ) : (
                                        <Tag color="error" style={{ borderRadius: '8px', padding: '0 8px', fontWeight: 700, fontSize: '9px' }}>OFFLINE</Tag>
                                    )}
                                </div>
                                <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '10px', fontWeight: 500 }}>ID: {viewModalOffer._id} • Premium Offer</p>
                            </div>
                        </div>

                        <div style={{ padding: '16px 20px' }}>
                            {/* Stats Row */}
                            <Row gutter={[12, 12]} style={{ marginBottom: '24px' }}>
                                <Col span={8}>
                                    <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', textAlign: 'center' }}>
                                        <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>OFFER CODE</div>
                                        <div style={{ color: '#4776E6', fontSize: '13px', fontWeight: 800, fontFamily: 'monospace' }}>{viewModalOffer.code || "N/A"}</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', textAlign: 'center' }}>
                                        <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>DISCOUNT VALUE</div>
                                        <div style={{ color: '#722ed1', fontSize: '13px', fontWeight: 800 }}>{viewModalOffer.discount || "0"}</div>
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', textAlign: 'center' }}>
                                        <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>TOTAL USES</div>
                                        <div style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 800 }}>{viewModalOffer.usedCount || 0}</div>
                                    </div>
                                </Col>
                            </Row>

                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div style={{ flex: 1.2 }}>
                                    <h3 style={{ fontSize: '11px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <InfoCircleOutlined style={{ color: '#4776E6' }} /> DESCRIPTION
                                    </h3>
                                    <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#fff', height: '90px', overflowY: 'auto' }}>
                                        <p style={{ margin: 0, color: '#475569', lineHeight: '1.4', fontSize: '11px' }}>{viewModalOffer.description}</p>
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ fontSize: '11px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <CalendarOutlined style={{ color: '#4776E6' }} /> VALIDITY
                                    </h3>
                                    <div style={{ padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#fff', height: '90px' }}>
                                        <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: '#64748b', fontWeight: 500, fontSize: '11px' }}>Expires</span>
                                            <span style={{ color: '#ef4444', fontWeight: 700, fontSize: '11px' }}>{viewModalOffer.expiryDate ? formatDate(viewModalOffer.expiryDate) : "Never"}</span>
                                        </div>
                                        <Divider style={{ margin: '6px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: '#64748b', fontWeight: 500, fontSize: '11px' }}>Created</span>
                                            <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '11px' }}>{formatDate(viewModalOffer.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Actions */}
                            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                <Popconfirm
                                    title="Are you sure you want to remove this offer?"
                                    onConfirm={() => {
                                        setViewModalVisible(false);
                                        handleDelete(viewModalOffer);
                                    }}
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button danger icon={<DeleteOutlined />} style={{ borderRadius: '10px', fontWeight: 600, fontSize: '12px' }}>Remove Offer</Button>
                                </Popconfirm>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Button
                                        icon={<EditOutlined style={{ color: '#4776E6' }} />}
                                        style={{ borderRadius: '10px', fontWeight: 600, color: '#4776E6', borderColor: '#4776E6', fontSize: '12px' }}
                                        onClick={() => {
                                            setViewModalVisible(false);
                                            handleEdit(viewModalOffer);
                                        }}
                                    >
                                        Edit Offer
                                    </Button>
                                    {/* <Button 
                                        type="primary" 
                                        style={{ borderRadius: '10px', fontWeight: 600, background: '#4776E6', fontSize: '12px' }}
                                        onClick={() => setViewModalVisible(false)}
                                    >
                                        Close View
                                    </Button> */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <AddOffer
                visible={addOfferModal}
                onCancel={() => setAddOfferModal(false)}
                onSuccess={() => {
                    setAddOfferModal(false);
                    fetchOffers();
                }}
                editing={isEditing}
                offerData={offerDetails}
                users={users}
            />
        </div>
    );
};

export default Offers;
